// context/RegistrationStatusContext.tsx
//
// バルク登録（バーコードスキャン等）の進捗状況をポーリングして保持するコンテキスト。
//
// バックエンドは登録リクエストごとに15秒デバウンス付きSQSで非同期処理するため、
// 連続スキャン中は反映が先延ばしされ続ける。この仕組みが見えないと「登録したのに
// 本棚に出てこない」と不安にさせてしまうため、常時ポーリングして進捗を可視化し、
// 完了を検知したら completionEventId をインクリメントして自動リフレッシュを促す。
//
// - アクティブ時（pending>0、または自分が直近登録した直後）: 高頻度でポーリング
// - アイドル時: 低頻度で継続（他デバイス・他タブでの登録開始も拾えるよう、完全停止はしない）
// - 3分以上pendingが解消しない場合はアイドル間隔にフォールバックし、無駄打ちを防ぐ
// - タブが非表示のときはポーリングを止め、復帰時に即時ポーリングする

import React, { createContext, useContext, ReactNode, useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getBulkStatus, BulkStatus } from '../utils/bibliographyApi';

const ACTIVE_INTERVAL_MS = 3000;
const IDLE_INTERVAL_MS = 45000;
const STUCK_THRESHOLD_MS = 3 * 60 * 1000;

interface PendingCounts {
  register: number;
  move: number;
  withdraw: number;
}

interface RegistrationStatusContextProps {
  hasPending: boolean;
  pendingCounts: PendingCounts;
  lastCompleted: BulkStatus['last_completed'];
  completionEventId: number;
  notifyLocalRegister: (count?: number) => void;
}

const RegistrationStatusContext = createContext<RegistrationStatusContextProps | undefined>(undefined);

export const RegistrationStatusProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isLoggedIn, idToken } = useAuth();

  const [hasPending, setHasPending] = useState(false);
  const [pendingCounts, setPendingCounts] = useState<PendingCounts>({ register: 0, move: 0, withdraw: 0 });
  const [lastCompleted, setLastCompleted] = useState<BulkStatus['last_completed']>(null);
  const [completionEventId, setCompletionEventId] = useState(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeSinceRef = useRef<number | null>(null);
  const lastSeenCompletedAtRef = useRef<number | null>(null);
  const hasPendingRef = useRef(false);
  // ポーリング関数がクロージャで古い idToken/isLoggedIn を掴んだままにならないよう ref で持つ
  const idTokenRef = useRef<string | null>(idToken);
  const isLoggedInRef = useRef(isLoggedIn);

  useEffect(() => { idTokenRef.current = idToken; }, [idToken]);
  useEffect(() => { isLoggedInRef.current = isLoggedIn; }, [isLoggedIn]);
  useEffect(() => { hasPendingRef.current = hasPending; }, [hasPending]);

  const scheduleNext = useCallback((immediate = false) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!isLoggedInRef.current || document.hidden) return;

    if (immediate) {
      timerRef.current = setTimeout(poll, 0);
      return;
    }

    let interval: number;
    if (hasPendingRef.current) {
      const stuck = activeSinceRef.current != null && Date.now() - activeSinceRef.current > STUCK_THRESHOLD_MS;
      interval = stuck ? IDLE_INTERVAL_MS : ACTIVE_INTERVAL_MS;
    } else {
      interval = IDLE_INTERVAL_MS;
    }
    timerRef.current = setTimeout(poll, interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const poll = useCallback(async () => {
    if (!isLoggedInRef.current || document.hidden) return;
    const token = idTokenRef.current;
    if (!token) {
      scheduleNext();
      return;
    }

    try {
      const status = await getBulkStatus(token);
      const counts: PendingCounts = {
        register: status.pending?.register?.count ?? 0,
        move: status.pending?.move?.count ?? 0,
        withdraw: status.pending?.withdraw?.count ?? 0,
      };
      const completedAt = status.last_completed?.completed_at ?? null;
      const justCompleted =
        !status.has_pending &&
        hasPendingRef.current &&
        completedAt != null &&
        completedAt !== lastSeenCompletedAtRef.current;

      setHasPending(status.has_pending);
      setPendingCounts(counts);
      activeSinceRef.current = status.has_pending ? (activeSinceRef.current ?? Date.now()) : null;

      if (justCompleted) {
        lastSeenCompletedAtRef.current = completedAt;
        setLastCompleted(status.last_completed);
        setCompletionEventId((n) => n + 1);
      }
    } catch (e) {
      // ポーリングの一時失敗は静かに無視し、次回に任せる
      console.warn('registration status poll failed', e);
    } finally {
      scheduleNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleNext]);

  const notifyLocalRegister = useCallback((count = 1) => {
    setHasPending(true);
    activeSinceRef.current = activeSinceRef.current ?? Date.now();
    setPendingCounts((prev) => ({ ...prev, register: prev.register + count }));
    // サーバー側の実態に早めに追いつく
    scheduleNext(true);
  }, [scheduleNext]);

  // ログイン状態の変化でポーリングの開始/停止
  useEffect(() => {
    if (isLoggedIn) {
      scheduleNext(true);
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
      setHasPending(false);
      setPendingCounts({ register: 0, move: 0, withdraw: 0 });
      activeSinceRef.current = null;
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // タブが非表示の間は止め、復帰時に即時ポーリング
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        scheduleNext(true);
      } else if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue: RegistrationStatusContextProps = {
    hasPending,
    pendingCounts,
    lastCompleted,
    completionEventId,
    notifyLocalRegister,
  };

  return (
    <RegistrationStatusContext.Provider value={contextValue}>
      {children}
    </RegistrationStatusContext.Provider>
  );
};

export const useRegistrationStatus = () => {
  const context = useContext(RegistrationStatusContext);
  if (context === undefined) {
    throw new Error('useRegistrationStatus must be used within a RegistrationStatusProvider');
  }
  return context;
};
